import axios from 'axios';
import { REGION_MAPPING } from './LocationService';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const normalizeSSHKey = (privateKey) => {
  if (!privateKey || privateKey.trim() === '') {
    return privateKey;
  }
  
  return privateKey
    .replace(/\\n/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();
};

export const addNode = async ({
  addressDetails,
  nodeType,
  sudoUserCreds,
  clientId,
  departmentIds = [],
  teamIds = [],
  userIds = [],
  token
}) => {
  try {
    const requestData = {
      client_id: clientId,
      department_ids: departmentIds,
      team_ids: teamIds,
      user_ids: userIds,
      address_details: {
        ip_address: addressDetails.ipAddress,
        port: parseInt(addressDetails.port),
        region: addressDetails.region
      },
      node_type: nodeType,
      sudo_user_creds: {
        username: sudoUserCreds.username,
        password: sudoUserCreds.password,
        private_key: normalizeSSHKey(sudoUserCreds.privateKey)
      }
    };

    console.log('Adding node with data:', requestData);

    const response = await axios.post(`${BASE_URL}/compute-v2/v2/node`, requestData, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json',
        'x-auth-user': token
      }
    });

    return {
      success: true,
      data: { id: response.data.id },
      status: response.status,
      message: 'Node added successfully'
    };
  } catch (error) {
    console.error('Add Node API Error:', error);
    return handleError(error);
  }
};

export const getNodeById = async (id, token) => {
  try {
    const response = await axios.get(`${BASE_URL}/compute-v2/v2/node/${id}`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'x-auth-user': token
      }
    });

    return {
      success: true,
      data: response.data.node,
      status: response.status,
      message: 'Node retrieved successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getNodes = async ({
  paginationParams = {},
  sortingParams = {},
  clientId,
  departmentId,
  teamId,
  userId,
  token
}) => {
  try {
    const params = new URLSearchParams();
    
    if (paginationParams.page !== undefined) params.append('pagination_params.page', paginationParams.page);
    if (paginationParams.page_size !== undefined) params.append('pagination_params.page_size', paginationParams.page_size);
    if (paginationParams.limit !== undefined) params.append('pagination_params.page_size', paginationParams.limit);
    if (sortingParams.sortBy) params.append('sort_by', sortingParams.sortBy);
    if (sortingParams.sortOrder) params.append('sort_order', sortingParams.sortOrder);
    if (clientId) params.append('client_id', clientId);
    if (departmentId) params.append('department_id', departmentId);
    if (teamId) params.append('team_id', teamId);
    if (userId) params.append('user_id', userId);

    const response = await axios.get(`${BASE_URL}/compute-v2/v2/nodes?${params.toString()}`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'x-auth-user': token
      }
    });

    return {
      success: true,
      data: {
        nodes: response.data.nodes || [],
        paginationResponse: response.data.pagination_response || {}
      },
      status: response.status,
      message: 'Nodes retrieved successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getNodeSSHDetailsById = async (id, token) => {
  try {
    const response = await axios.get(`${BASE_URL}/compute-v2/v2/node/${id}/ssh`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'x-auth-user': token
      }
    });

    return {
      success: true,
      data: {
        addressDetails: response.data.address_details,
        sudoUserCreds: response.data.sudo_user_creds
      },
      status: response.status,
      message: 'SSH details retrieved successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};

export const updateNode = async ({
  id,
  clientId,
  departmentIds,
  teamIds,
  userIds,
  addressDetails,
  nodeType,
  sudoUserCreds,
  name,
  hostname,
  token
}) => {
  try {
    const requestData = {};

    if (clientId !== undefined) requestData.client_id = clientId;
    if (departmentIds !== undefined) requestData.department_ids = departmentIds;
    if (teamIds !== undefined) requestData.team_ids = teamIds;
    if (userIds !== undefined) requestData.user_ids = userIds;
    
    if (addressDetails) {
      requestData.address_details = {
        ...(addressDetails.ipAddress !== undefined ? { ip_address: addressDetails.ipAddress } : {}),
        ...(addressDetails.port !== undefined ? { port: parseInt(addressDetails.port) } : {}),
        ...(addressDetails.region !== undefined ? { region: addressDetails.region } : {}),
        ...(addressDetails.hostname !== undefined ? { hostname: addressDetails.hostname } : {})
      };
    } else if (hostname !== undefined) {
      requestData.address_details = {
        hostname: hostname
      };
    }
    
    if (nodeType !== undefined) requestData.node_type = nodeType;
    
    if (sudoUserCreds) {
      requestData.sudo_user_creds = {
        username: sudoUserCreds.username,
        password: sudoUserCreds.password,
        private_key: normalizeSSHKey(sudoUserCreds.privateKey)
      };
    }
    
    if (name !== undefined) requestData.name = name;

    const response = await axios.patch(
      `${BASE_URL}/compute-v2/v2/node/${id}`, 
      requestData,
      { 
        headers: { 
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json',
          'x-auth-user': token
        }
      }
    );

    return {
      success: true,
      data: response.data.node,
      status: response.status,
      message: 'Node updated successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};

export const deleteNode = async (id, token) => {
  try {
    const response = await axios.delete(`${BASE_URL}/compute-v2/v2/node/${id}`, {
      headers: { 
        'Authorization': 'Bearer ' + token,
        'x-auth-user': token
      }
    });

    return {
      success: true,
      status: response.status,
      message: 'Node deleted successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};

export const NodeType = {
  NODE_TYPE_UNSPECIFIED: 0,
  NODE_TYPE_STORAGE: 1,
  NODE_TYPE_COMPUTE: 2
};

export const NodeStatus = {
  NODE_STATUS_UNSPECIFIED: 0,
  NODE_STATUS_ACTIVE: 1,
  NODE_STATUS_INACTIVE: 2,
  NODE_STATUS_MAINTENANCE: 3,
  NODE_STATUS_REGISTRATION_STARTED: "NODE_STATUS_REGISTRATION_STARTED",
  NODE_STATUS_REGISTRATION_FAILED: "NODE_STATUS_REGISTRATION_FAILED"
};

// Validate and normalize region
export const validateRegion = (region) => {
  if (!region) return 'us-east-1'; // Default fallback

  // Check if the region exists in our mapping
  if (REGION_MAPPING[region]) {
    return region;
  }

  // Try to find a similar region (e.g., us-east -> us-east-1)
  const similarRegion = Object.keys(REGION_MAPPING).find(r =>
    r.startsWith(region.split('-').slice(0, 2).join('-'))
  );

  return similarRegion || 'us-east-1'; // Final fallback
};

export const transformNodeToDisplayFormat = (node) => {
  if (!node) return null;
  
  const getStatusText = (status) => {
    switch (status) {
      case NodeStatus.NODE_STATUS_ACTIVE:
      case "NODE_STATUS_ACTIVE":
      case 1: 
        return 'Active';
      case NodeStatus.NODE_STATUS_INACTIVE:
      case "NODE_STATUS_INACTIVE":
      case 2: 
        return 'Offline';
      case NodeStatus.NODE_STATUS_MAINTENANCE:
      case "NODE_STATUS_MAINTENANCE":
      case 3: 
        return 'Maintenance';
      case NodeStatus.NODE_STATUS_REGISTRATION_STARTED:
      case "NODE_STATUS_REGISTRATION_STARTED":
        return 'Registering';
      case NodeStatus.NODE_STATUS_REGISTRATION_FAILED:
      case "NODE_STATUS_REGISTRATION_FAILED":
        return 'Registration Failed';
      default: 
        return 'Unknown';
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case NodeType.NODE_TYPE_COMPUTE: return 'Compute';
      case NodeType.NODE_TYPE_STORAGE: return 'Storage';
      default: return 'Unknown';
    }
  };

  const getCpuModel = (nodeMetadata) => {
    return nodeMetadata?.cpu_info?.model || 'Unknown CPU';
  };

  const getGpuModel = (nodeMetadata, gpus) => {
    if (nodeMetadata?.gpu_info?.driver?.length > 0) {
      const firstGpu = nodeMetadata.gpu_info.driver[0];
      return firstGpu.model || 'Unknown GPU';
    }
    if (gpus && gpus.length > 0 && gpus[0].model) {
      return gpus[0].model.replace(/_/g, ' ').toUpperCase();
    }
    return null;
  };

  const getGpuBreakdown = (nodeMetadata, gpus) => {
    const gpuMap = new Map();

    // Process GPU info from nodeMetadata
    if (nodeMetadata?.gpu_info?.driver?.length > 0) {
      nodeMetadata.gpu_info.driver.forEach(gpu => {
        const model = gpu.model || 'Unknown GPU';
        gpuMap.set(model, (gpuMap.get(model) || 0) + 1);
      });
    } else if (gpus && gpus.length > 0) {
      // Fallback to gpus array
      gpus.forEach(gpu => {
        const model = gpu.model ? gpu.model.replace(/_/g, ' ').toUpperCase() : 'Unknown GPU';
        gpuMap.set(model, (gpuMap.get(model) || 0) + 1);
      });
    }

    return Array.from(gpuMap.entries()).map(([model, count]) => ({ model, count }));
  };

  const getTotalGpuCount = (nodeMetadata, gpus) => {
    // First try to get count from nodeMetadata
    if (nodeMetadata?.gpu_info?.driver?.length > 0) {
      return nodeMetadata.gpu_info.driver.length;
    }
    // Fallback to gpus array length
    if (gpus && gpus.length > 0) {
      return gpus.length;
    }
    // Final fallback to top-level num_of_gpus
    return node.num_of_gpus || node.numOfGpus || 0;
  };

  const formatGpuDisplay = (gpuBreakdown) => {
    if (!gpuBreakdown || gpuBreakdown.length === 0) return null;
    
    if (gpuBreakdown.length === 1) {
      const { model, count } = gpuBreakdown[0];
      return count > 1 ? `${model} ×${count}` : model;
    }
    
    return gpuBreakdown.map(({ model, count }) => 
      count > 1 ? `${model} ×${count}` : model
    ).join(', ');
  };

  const getMemoryInfo = (nodeMetadata) => {
    const memoryTotal = nodeMetadata?.memory_info?.total;
    if (memoryTotal) {
      const memoryStr = memoryTotal.toString();
      const match = memoryStr.match(/(\d+\.?\d*)\s*([KMGT]?B?)/i);
      
      if (match) {
        const value = parseFloat(match[1]);
        const unit = match[2]?.toUpperCase().replace('B', '') || '';
        
        let sizeInGB = value;
        switch (unit) {
          case 'T':
            sizeInGB = value * 1024;
            break;
          case 'G':
          case '':
            sizeInGB = value;
            break;
          case 'M':
            sizeInGB = value / 1024;
            break;
          case 'K':
            sizeInGB = value / (1024 * 1024);
            break;
          default:
            sizeInGB = value;
        }
        
        return Math.round(sizeInGB);
      }
    }
    return node.memory || 0;
  };

  const getStorageInfo = (nodeMetadata) => {
    if (nodeMetadata?.storage_info?.disks?.length > 0) {
      const totalSize = nodeMetadata.storage_info.disks.reduce((total, disk) => {
        if (!disk.size) return total;
        
        const sizeStr = disk.size.toString();
        const match = sizeStr.match(/(\d+\.?\d*)\s*([KMGT]?)/i);
        
        if (!match) return total;
        
        const value = parseFloat(match[1]);
        const unit = match[2]?.toUpperCase() || '';
        
        let sizeInGB = value;
        switch (unit) {
          case 'T':
            sizeInGB = value * 1024;
            break;
          case 'G':
            sizeInGB = value;
            break;
          case 'M':
            sizeInGB = value / 1024;
            break;
          case 'K':
            sizeInGB = value / (1024 * 1024);
            break;
          default:
            sizeInGB = value;
        }
        
        return total + sizeInGB;
      }, 0);
      return Math.round(totalSize);
    }
    return node.storage || 0;
  };

  const getPublicIpAddress = (nodeMetadata) => {
    return nodeMetadata?.network_info?.addresses?.public || 
           nodeMetadata?.network_info?.interfaces?.find(iface => 
             iface.ip_addresses?.some(ip => !ip.startsWith('192.168') && !ip.startsWith('10.') && !ip.startsWith('172.') && !ip.includes(':'))
           )?.ip_addresses?.find(ip => !ip.startsWith('192.168') && !ip.startsWith('10.') && !ip.startsWith('172.') && !ip.includes(':')) || 
           'Unknown';
  };

  const getHostname = (nodeMetadata) => {
    return nodeMetadata?.system_info?.hostname || `node-${node.id?.substring(0, 8)}`;
  };

  const getUptimeInfo = (nodeMetadata) => {
    return nodeMetadata?.uptime_info?.human || 'Unknown';
  };

  const getClockSpeed = (nodeMetadata) => {
    const clockSpeed = nodeMetadata?.cpu_info?.clock_speed;
    if (clockSpeed) {
      return clockSpeed.replace(/(\d+\.?\d*)\s*MHz/, '$1 MHz');
    }
    return 'Unknown';
  };

  const isRegistering = (status) => {
    return status === "NODE_STATUS_REGISTRATION_STARTED" || status === NodeStatus.NODE_STATUS_REGISTRATION_STARTED;
  };

  const isRegistrationFailed = (status) => {
    return status === "NODE_STATUS_REGISTRATION_FAILED" || status === NodeStatus.NODE_STATUS_REGISTRATION_FAILED;
  };

  return {
    id: node.id,
    nodeId: node.id,
    name: node.name,
    hostname: getHostname(node.nodeMetadata),
    ipAddress: getPublicIpAddress(node.nodeMetadata),
    port: node.address_details?.port || node.addressDetails?.port || '22',
    region: validateRegion(node.address_details?.region || node.addressDetails?.region),
    location: validateRegion(node.address_details?.region || node.addressDetails?.region),
    status: getStatusText(node.status),
    rawStatus: node.status,
    isRegistering: isRegistering(node.status),
    isRegistrationFailed: isRegistrationFailed(node.status),
    type: getTypeText(node.type),
    singleNode: node.single_node || node.singleNode || false,
    slurmHead: node.slurm_head || node.slurmHead || false,
    slurmOwner: node.slurm_owner || node.slurmOwner || '',
    numCpus: node.nodeMetadata?.cpu_info?.cores || node.num_of_cpus || node.numOfCpus || 0,
    numOfCpus: node.nodeMetadata?.cpu_info?.cores || node.num_of_cpus || node.numOfCpus || 0,
    cpus: node.nodeMetadata?.cpu_info?.cores || node.num_of_cpus || node.numOfCpus || 0,
    numGpus: getTotalGpuCount(node.nodeMetadata, node.gpus),
    memory: getMemoryInfo(node.nodeMetadata),
    mem: getMemoryInfo(node.nodeMetadata),
    storage: node.storage || getStorageInfo(node.nodeMetadata),
    os: node.os || node.nodeMetadata?.os_info?.name || 'Unknown',
    osVersion: node.os_version || node.osVersion || node.nodeMetadata?.os_info?.version || '',
    kernelVersion: node.kernel_version || node.kernelVersion || node.nodeMetadata?.os_info?.kernel || '',
    gpus: node.gpus || [],
    nodeMetadata: node.node_metadata || node.nodeMetadata,
    createdAt: node.created_at || node.createdAt,
    updatedAt: node.updated_at || node.updatedAt,
    specification: `${node.nodeMetadata?.cpu_info?.cores || node.num_of_cpus || node.numOfCpus || 0} CPUs, ${getMemoryInfo(node.nodeMetadata)}GB RAM`,
    function: node.slurm_head || node.slurmHead ? "Slurm Head Node" : getTypeText(node.type) + " Node",
    health: isRegistrationFailed(node.status) ? "Registration Failed" : "Good",
    coordinates: [0, 0],
    machineId: node.id,
    gpuType: node.gpus && node.gpus.length > 0 ? node.gpus[0].model : "N/A",
    gpu: node.gpus && node.gpus.length > 0 ? getGpuVendorText(node.gpus[0].vendor) : "None",
    vram: calculateTotalVRAM(node.gpus),
    cpuModel: getCpuModel(node.nodeMetadata),
    clockSpeed: getClockSpeed(node.nodeMetadata),
    uptime: getUptimeInfo(node.nodeMetadata),
    cpuCores: node.nodeMetadata?.cpu_info?.cores || node.num_of_cpus || node.numOfCpus || 0,
    cpuThreads: node.nodeMetadata?.cpu_info?.threads || 0,
    architecture: node.nodeMetadata?.os_info?.architecture || 'Unknown',
    gpuModel: getGpuModel(node.nodeMetadata, node.gpus),
    gpuBreakdown: getGpuBreakdown(node.nodeMetadata, node.gpus),
    gpuDisplayText: formatGpuDisplay(getGpuBreakdown(node.nodeMetadata, node.gpus))
  };
};

const getGpuVendorText = (vendor) => {
  switch (vendor) {
    case 1: return 'NVIDIA';
    case 2: return 'AMD';
    case 3: return 'Intel';
    default: return 'Unknown';
  }
};

const calculateTotalVRAM = (gpus) => {
  if (!gpus || gpus.length === 0) return 0;
  return gpus.length * 8;
};

export const createNodeRequest = (formData) => {
  return {
    addressDetails: {
      ipAddress: formData.ipAddress,
      port: formData.port,
      region: formData.region
    },
    nodeType: parseInt(formData.nodeType) || NodeType.NODE_TYPE_COMPUTE,
    sudoUserCreds: {
      username: formData.username,
      password: formData.password,
      privateKey: normalizeSSHKey(formData.privateKey)
    },
    clientId: formData.clientId,
    departmentIds: formData.departmentIds || [],
    teamIds: formData.teamIds || [],
    userIds: formData.userIds || []
  };
};

const handleError = (error) => {
  console.error('Node API Error:', error);
  
  if (error.response?.status === 503) {
    return {
      success: false,
      error: 'Service temporarily unavailable'
    };
  }

  if (error.response?.status === 401) {
    return {
      success: false,
      error: 'Unauthorized - Invalid credentials'
    };
  }

  if (error.response?.status === 400) {
    return {
      success: false,
      error: error.response?.data?.message || 'Bad request - Invalid data provided'
    };
  }
  
  return {
    success: false,
    error: error.response?.data?.message || error.message || 'Operation failed'
  };
}; 